import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck,
  Fingerprint,
  ChatCircleDots,
  ArrowRight,
  CheckCircle,
  AppleLogo,
  GooglePlayLogo,
  LockKey,
  Sparkle,
} from "@phosphor-icons/react";

import api from "@/lib/api";
import { PageShell } from "@/components/Layout";

const HERO_BG =
  "https://images.unsplash.com/photo-1653259038915-7cf0b7a4dd6c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const FEATURES = [
  {
    icon: <Fingerprint size={22} weight="thin" />,
    title: "100% ID verified profiles",
    body: "Every member is verified with government-issued ID and a live selfie match — no fakes, no timewasters, no ghosts. Reverification required every 6 months.",
  },
  {
    icon: <ShieldCheck size={22} weight="thin" />,
    title: "Offender checks",
    body: "We run background checks against public offender registers before anyone is approved. Safety-first, always.",
  },
  {
    icon: <ChatCircleDots size={22} weight="thin" />,
    title: "Profiles, DMs & instant messaging",
    body: "Everything in one beautifully designed app — browse, match, chat and video call without leaving the platform.",
  },
];

const HOW_IT_WORKS = [
  { n: "01", t: "Get verified", d: "One-time ID + selfie verification unlocks the community. Your documents are encrypted and never shared." },
  { n: "02", t: "Sign up in minutes", d: "Quick, discreet sign-up. Choose your handle, upload photos and set what you're looking for." },
  { n: "03", t: "Meet real people", d: "Discover UK swingers near you and start conversations knowing every profile is a real, verified human." },
];

const WaitlistForm = ({ compact = false }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/waitlist", { email: email.trim() });
      setDone(true);
      toast.success("You're on the list");
    } catch {
      toast.error("Could not add you to the list");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className={`inline-flex items-center gap-2 ${compact ? "text-sm" : "text-base"} text-[#e2a9f1]`}
        data-testid="waitlist-success"
      >
        <CheckCircle size={compact ? 16 : 20} weight="thin" />
        You&rsquo;re on the list — welcome to Swingle.
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`flex flex-col sm:flex-row gap-3 ${compact ? "max-w-md" : "max-w-lg"}`}
      data-testid={compact ? "waitlist-form-compact" : "waitlist-form"}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#e2a9f1] focus:outline-none px-5 py-3 text-[#EDEDED] rounded-sm placeholder:text-[#444]"
        data-testid={compact ? "waitlist-email-input-compact" : "waitlist-email-input"}
      />
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center justify-center gap-2 bg-[#e2a9f1] text-[#0A0A0A] font-semibold tracking-wide hover:bg-[#efc8f7] transition-colors duration-300 rounded-sm px-6 py-3 disabled:opacity-40"
        data-testid={compact ? "waitlist-submit-compact" : "waitlist-submit"}
      >
        {submitting ? "Adding…" : "Join the Waitlist"}
        <ArrowRight size={16} weight="bold" />
      </button>
    </form>
  );
};

// Stylised phone mockup
const PhoneMockup = () => (
  <div className="relative mx-auto w-[280px] md:w-[320px] aspect-[9/19] rounded-[40px] bg-[#131313] border border-[#2A2A2A] shadow-[0_40px_120px_-20px_rgba(226,169,241,0.4)] overflow-hidden">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#0A0A0A] rounded-b-2xl z-20" />
    <div className="relative w-full h-full pt-10 px-5 pb-6 flex flex-col">
      {/* App header */}
      <div className="flex items-center justify-between mb-6">
        <img src="/swingle-logo.png" alt="Swingle" className="h-8 w-auto" />
        <div className="w-8 h-8 rounded-full bg-[#e2a9f1]/20 border border-[#e2a9f1]/40" />
      </div>
      {/* Profile card */}
      <div className="relative flex-1 rounded-2xl bg-gradient-to-br from-[#2a1a2f] via-[#1a1520] to-[#0A0A0A] overflow-hidden border border-[#2A2A2A]">
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={14} weight="fill" className="text-[#e2a9f1]" />
            <span className="text-[10px] uppercase tracking-widest text-[#e2a9f1]">ID Verified</span>
          </div>
          <div className="text-xl font-serif-display text-white">Ava &amp; Jonas</div>
          <div className="text-xs text-white/60">Manchester · 3 miles away</div>
        </div>
      </div>
      {/* Action row */}
      <div className="flex items-center justify-around mt-4">
        <div className="w-11 h-11 rounded-full bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center text-[#A3A3A3]">✕</div>
        <div className="w-14 h-14 rounded-full bg-[#e2a9f1] flex items-center justify-center text-[#0A0A0A]">
          <Sparkle size={22} weight="fill" />
        </div>
        <div className="w-11 h-11 rounded-full bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center text-[#e2a9f1]">
          <ChatCircleDots size={18} weight="thin" />
        </div>
      </div>
    </div>
  </div>
);

export default function Home() {
  const [waitlistCount, setWaitlistCount] = useState(null);

  useEffect(() => {
    api.get("/waitlist/count").then((r) => setWaitlistCount(r.data.count)).catch(() => {});
  }, []);

  return (
    <PageShell>
      {/* Hero */}
      <section
        className="relative min-h-[92vh] flex items-center overflow-hidden -mt-36 md:-mt-44"
        data-testid="hero-section"
      >
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/60 to-[#0A0A0A]" />
          <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-[#e2a9f1]/10 blur-[140px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-40 pb-24 w-full">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="lg:col-span-7"
            >
              <div className="font-mono-overline text-[#e2a9f1] mb-6" data-testid="hero-overline">
                Coming Soon · UK · iOS &amp; Android
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif-display text-[#EDEDED] leading-[0.95] mb-8">
                Bigger. Better.
                <br />
                <span className="display-italic text-[#e2a9f1]">&amp; Safer Than Anywhere You&rsquo;ve Been Before.</span>
              </h1>
              <p className="text-lg text-[#EDEDED]/80 leading-relaxed max-w-xl mb-10">
                Swingle is a beautifully designed app for UK Swingers. 100% ID-verified
                profiles that needs to be completed every 6 months, offender-checked members and
                a proper messaging experience — no more bouncing between multiple apps to meet
                real people like you.
              </p>
              <WaitlistForm />
              <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-[#A3A3A3]">
                <div className="flex items-center gap-2">
                  <LockKey size={14} weight="thin" className="text-[#e2a9f1]" />
                  Discreet by default
                </div>
                <div className="flex items-center gap-2">
                  <Sparkle size={14} weight="thin" className="text-[#e2a9f1]" />
                  500+ already on the waitlist
                </div>
              </div>

              {/* Store badges */}
              <div className="mt-8 flex flex-wrap gap-4">
                <div
                  className="inline-flex items-center gap-3 bg-[#131313] border border-[#2A2A2A] rounded-md px-5 py-3 cursor-not-allowed opacity-90"
                  data-testid="app-store-badge"
                  aria-label="Coming soon on the App Store"
                >
                  <AppleLogo size={30} weight="fill" className="text-[#EDEDED]" />
                  <div className="flex flex-col leading-tight">
                    <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Coming soon on</span>
                    <span className="text-lg text-[#EDEDED] font-semibold -mt-0.5">The App Store</span>
                  </div>
                </div>
                <div
                  className="inline-flex items-center gap-3 bg-[#131313] border border-[#2A2A2A] rounded-md px-5 py-3 cursor-not-allowed opacity-90"
                  data-testid="play-store-badge"
                  aria-label="Coming soon on Google Play"
                >
                  <GooglePlayLogo size={28} weight="fill" className="text-[#EDEDED]" />
                  <div className="flex flex-col leading-tight">
                    <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Coming soon on</span>
                    <span className="text-lg text-[#EDEDED] font-semibold -mt-0.5">Google Play</span>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.15 }}
              className="lg:col-span-5 flex justify-center"
            >
              <PhoneMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 lg:px-12 border-t border-[#2A2A2A]" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="font-mono-overline text-[#e2a9f1] mb-4">What makes Swingle different</div>
          <h2 className="text-4xl md:text-5xl font-serif-display text-[#EDEDED] leading-tight mb-16 max-w-3xl">
            No fakes. No timewasters.
            <br />
            <span className="display-italic text-[#e2a9f1]">Just real people.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-[#131313] border border-[#2A2A2A] p-8 hover:border-[#e2a9f1]/40 transition-colors"
                data-testid={`feature-${i}`}
              >
                <div className="text-[#e2a9f1] mb-5">{f.icon}</div>
                <h3 className="text-2xl font-serif-display text-[#EDEDED] mb-3 leading-tight">
                  {f.title}
                </h3>
                <p className="text-[#A3A3A3] leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 lg:px-12 border-t border-[#2A2A2A]" data-testid="how-section">
        <div className="max-w-7xl mx-auto">
          <div className="font-mono-overline text-[#e2a9f1] mb-4">How Swingle works</div>
          <h2 className="text-4xl md:text-5xl font-serif-display text-[#EDEDED] mb-16 max-w-2xl">
            Three steps to a real connection.
          </h2>
          <div className="grid md:grid-cols-3 gap-12">
            {HOW_IT_WORKS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                data-testid={`how-step-${s.n}`}
              >
                <div className="font-brand text-5xl text-[#e2a9f1]/70 mb-6">{s.n}</div>
                <h3 className="text-2xl font-serif-display text-[#EDEDED] mb-4">{s.t}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust block */}
      <section className="py-24 px-6 lg:px-12 border-t border-[#2A2A2A]" data-testid="trust-section">
        <div className="max-w-4xl mx-auto text-center">
          <ShieldCheck size={40} weight="thin" className="text-[#e2a9f1] mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-serif-display text-[#EDEDED] leading-tight mb-6">
            Safer than anywhere
            <br />
            <span className="display-italic text-[#e2a9f1]">you&rsquo;ve been before.</span>
          </h2>
          <p className="text-[#A3A3A3] leading-relaxed max-w-2xl mx-auto">
            Every single member is ID-verified against a government-issued document with a live
            selfie match. Every application is screened against public offender registers before
            approval. Your documents are encrypted, never shared, and you always control what other
            members can see.
          </p>
          <div className="mt-10 inline-flex items-center gap-3 px-5 py-3 border border-[#2A2A2A] rounded-sm" data-testid="powered-by-veriff">
            <span className="font-mono-overline text-[#A3A3A3]">Powered by</span>
            <span className="text-2xl font-serif-display text-[#EDEDED] tracking-tight">Veriff</span>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="final-cta" className="py-28 px-6 lg:px-12 border-t border-[#2A2A2A] scroll-mt-32" data-testid="final-cta">
        <div className="max-w-4xl mx-auto text-center">
          <div className="font-mono-overline text-[#e2a9f1] mb-4">Be first in line</div>
          <h2 className="text-4xl md:text-6xl font-serif-display text-[#EDEDED] leading-tight mb-8">
            Join the Swingle
            <br />
            <span className="display-italic text-[#e2a9f1]">waitlist.</span>
          </h2>
          <p className="text-[#A3A3A3] leading-relaxed max-w-xl mx-auto mb-10">
            We&rsquo;ll email you the moment Swingle opens for signups — plus early-access invites
            for the first cohort of verified members.
          </p>
          <div className="flex justify-center">
            <WaitlistForm />
          </div>
        </div>
      </section>
    </PageShell>
  );
}
